import { useEffect, useState } from "react";
import { prefersReducedMotion } from "../../motion";

// How long the button has shown whole in the window before the invitation
// plays: time to read the headline and the caption first.
export const DELAY = 2000;
// How much of the button counts as whole, so subpixel rounding does not
// keep it from ever being whole.
const WHOLE = 0.98;

// PROTOTYPE (hero shapes): when the hero's invitation to press its button
// (`invite`, from invite.js) plays: once, DELAY after the button first shows
// whole in the window, while the story still `waiting` for it; it stops the
// moment the story starts, and never plays for those who ask for less
// motion. Returns a callback ref for the button and, while it plays, how far
// into it it is, in ms; null otherwise.
export default function useInvite(invite, waiting) {
  const [button, setButton] = useState(null);
  const [due, setDue] = useState(false);
  const [at, setAt] = useState(null);

  useEffect(() => {
    if (!button || due || prefersReducedMotion()) return undefined;
    let timer;
    const observer = new IntersectionObserver(
      ([entry]) => {
        clearTimeout(timer);
        if (entry.intersectionRatio >= WHOLE) timer = setTimeout(() => setDue(true), DELAY);
      },
      { threshold: [0, WHOLE] },
    );
    observer.observe(button);
    return () => {
      observer.disconnect();
      clearTimeout(timer);
    };
  }, [button, due]);

  useEffect(() => {
    if (!due || !waiting) return undefined;
    const start = performance.now();
    let frame = requestAnimationFrame(function tick(now) {
      const t = now - start;
      if (t >= invite.ms) {
        setAt(null);
        return;
      }
      setAt(t);
      frame = requestAnimationFrame(tick);
    });
    return () => {
      cancelAnimationFrame(frame);
      setAt(null);
    };
  }, [due, waiting, invite]);

  return { watch: setButton, at };
}
